import axiosInstance from "../axios/index";
import { getCookie, setCookie } from "../utils/cookiesUtils";
import { List_const } from "../consts/index";

declare const grecaptcha: any;

//#region GET
//  GET một item
export async function asyncGetItem<T>({
  collection_name,
  id,
  options = {},
  access_token,
  signal,
}: {
  collection_name: string;
  id?: string; // tìm kiếm không cần ID
  options?: Record<string, any>;
  access_token?: string;
  signal?: AbortSignal;
}): Promise<T> {
  if (!collection_name) {
    throw new Error("collection_name is required");
  }

  const idApp = getIdApp(collection_name, options);
  let url = `${prepareURL(
    collection_name,
    idApp,
    access_token || process.env.public_token || "",
  )}`;

  if (id) {
    // Nếu có ID, truy vấn theo ID
    url += `/${id}`;
  } else if (options.condition) {
    // Nếu không có ID, truy vấn bằng condition
    url += `&q=${encodeURIComponent(JSON.stringify(options.condition))}`;
  }

  // Thêm fields nếu có
  if (options.fields) {
    url += `&fields=${options.fields}`;
  }
  // console.log("url get item" + url);

  try {
    const res = await axiosInstance.get<T>(url, {
      headers: { uid: getCookie("uid") },
      ...(signal ? { signal } : {}),
    });
    // console.log("getItemres " + JSON.stringify(res.data, null, 2));

    return res.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.error || error.message);
  }
}

//  GET danh sách
export async function asyncGetList<T>({
  collection_name,
  options = {},
  access_token,
  signal,
}: {
  collection_name: string;
  options?: any;
  access_token?: string;
  signal?: AbortSignal;
}): Promise<T> {
  if (!collection_name) throw new Error("collection_name is required");
  const idApp = getIdApp(collection_name, options);
  let url = prepareURL(
    collection_name,
    idApp,
    access_token || process.env.public_token || "",
  );
  url = appendOptionsToURL(url, options);

  const res = await axiosInstance.get<T>(url, {
    ...(signal ? { signal } : {}),
    headers: { uid: getCookie("uid") },
  });

  return res.data;
}

//#endregion
//#region POST
//  POST một item
export async function asyncPostItem<T>({
  collection_name,
  data,
  access_token,
  require_recaptcha = false,
  action = "POST",
}: {
  collection_name: string;
  data: any;
  access_token?: string;
  require_recaptcha?: boolean;
  action?: string;
}): Promise<T> {
  if (!collection_name || !data)
    throw new Error("Thiếu collection hoặc dữ liệu");

  // Xác định id_app nếu cần
  const _id_app = getIdApp(collection_name, {});
  let url = `${process.env.server_url_report}/api${
    _id_app ? `/${_id_app}` : ""
  }/${collection_name}`;
  url = `${url}?access_token=${access_token || process.env.public_token}`;

  // Thêm trustkey nếu có
  if (process.env.trustkey) {
    url += `&trustkey=${process.env.trustkey}`;
  }

  try {
    // ✅ Nếu yêu cầu reCAPTCHA, xử lý trước khi gửi request
    if (require_recaptcha && typeof grecaptcha !== "undefined") {
      const token = await grecaptcha.execute(
        process.env.GOOGLE_RECAPTCHA_SITE_KEY,
        { action },
      );
      data["g-recaptcha-response"] = token;
    }

    // ✅ Gửi request với axios
    const res = await axiosInstance.post<T>(url, data, {
      headers: { uid: getCookie("uid") },
    });

    // Lưu UID vào cookie nếu có
    let _uid: string | undefined;
    if (typeof res.headers.get === "function") {
      _uid = res.headers.get("uid") || undefined;
    } else if ("uid" in res.headers) {
      _uid = (res.headers as any)["uid"];
    }
    if (_uid) setCookie("uid", _uid, 1);

    return res.data;
  } catch (error: any) {
    console.error(
      "🚀 ~ asyncPostItem ERROR:",
      error?.response?.data || error.message,
    );
    throw new Error(error?.response?.data?.error || error.message);
  }
}

//  POST danh sách
export async function asyncPostList<T>({
  collection_name,
  data,
  access_token,
}: {
  collection_name: string;
  data: any[];
  access_token?: string;
}): Promise<T> {
  if (!collection_name || !Array.isArray(data))
    throw new Error("Dữ liệu phải là một mảng");

  const _id_app = getIdApp(collection_name, {});
  let url = `${process.env.server_url_report}/api${
    _id_app ? `/${_id_app}` : ""
  }/${collection_name}`;
  url = `${url}?access_token=${access_token || process.env.public_token}`;

  try {
    const res = await axiosInstance.post<T>(
      url,
      { items: data },
      {
        headers: { uid: getCookie("uid") },
      },
    );

    let _uid: string | undefined;
    if (typeof res.headers.get === "function") {
      _uid = res.headers.get("uid") || undefined;
    } else if ("uid" in res.headers) {
      _uid = (res.headers as any)["uid"];
    }
    if (_uid) setCookie("uid", _uid, 1);

    return res.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.error || error.message);
  }
}

//#endregion
//#region PUT
//  PUT cập nhật một item
export async function asyncPutItem<T>({
  collection_name,
  id,
  data,
  access_token,
}: {
  collection_name: string;
  id: string;
  data: any;
  access_token?: string;
}): Promise<T> {
  if (!collection_name || !id || !data)
    throw new Error("collection_name, id, and data are required");
  const idApp = getIdApp(collection_name);
  const url = `${prepareURL(
    collection_name,
    idApp,
    access_token || process.env.public_token || "",
  )}/${id}`;

  const res = await axiosInstance.put<T>(url, data, {
    headers: { uid: getCookie("uid") },
  });

  return res.data;
}
//#endregion
//#region DELETE
//  Xóa một mục theo ID
export async function asyncDeleteItem<T>({
  collection_name,
  id,
  access_token,
  options = {},
}: {
  collection_name: string;
  id: string;
  access_token?: string;
  options?: Record<string, any>;
}): Promise<T> {
  if (!id) throw new Error("Thiếu ID để xóa");

  const _id_app = getIdApp(collection_name, options);
  let url = `${process.env.server_url_report}/api${
    _id_app ? `/${_id_app}` : ""
  }/${collection_name}/${id}`;
  url = `${url}?access_token=${access_token || process.env.public_token}`;

  if (options.otp_id) url += `&otp-id=${options.otp_id}`;
  if (options.otp_code) url += `&otp-code=${options.otp_code}`;
  if (process.env.trustkey) url += `&trustkey=${process.env.trustkey}`;

  try {
    const res = await axiosInstance.delete<T>(url, {
      headers: { uid: getCookie("uid") },
    });

    let _uid: string | undefined;
    if (typeof res.headers.get === "function") {
      _uid = res.headers.get("uid") || undefined;
    } else if ("uid" in res.headers) {
      _uid = (res.headers as any)["uid"];
    }
    if (_uid) setCookie("uid", _uid, 1);

    return res.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.error || error.message);
  }
}

//  Xóa nhiều mục theo điều kiện
export async function asyncDeleteList<T>({
  collection_name,
  ids,
  access_token,
  options = {},
}: {
  collection_name: string;
  ids: string[]; // Danh sách ID cần xóa
  access_token?: string;
  options?: Record<string, any>;
}): Promise<T> {
  if (!ids || ids.length === 0) throw new Error("Thiếu danh sách ID để xóa");

  const _id_app = getIdApp(collection_name, options);
  let url = `${process.env.server_url_report}/api${
    _id_app ? `/${_id_app}` : ""
  }/${collection_name}`;
  url = `${url}?access_token=${access_token || process.env.public_token}`;

  if (options.otp_id) url += `&otp-id=${options.otp_id}`;
  if (options.otp_code) url += `&otp-code=${options.otp_code}`;
  if (process.env.trustkey) url += `&trustkey=${process.env.trustkey}`;

  try {
    const res = await axiosInstance.delete<T>(url, {
      headers: {
        uid: getCookie("uid"),
      },
      data: { ids }, // Truyền danh sách ID trong body
    });

    let _uid: string | undefined;
    if (typeof res.headers.get === "function") {
      _uid = res.headers.get("uid") || undefined;
    } else if ("uid" in res.headers) {
      _uid = (res.headers as any)["uid"];
    }
    if (_uid) setCookie("uid", _uid, 1);

    return res.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.error || error.message);
  }
}
//#endregion

//#region HELPER FUNCTIONS
//  Lấy id_app tự động
function getIdApp(collectionName: string, options?: any): string {
  return List_const.list_not_require_id_app_default.includes(collectionName)
    ? ""
    : options?.id_app || process.env.id_app;
}

//  Tạo URL API cho `GET`, `POST`, `PUT`, `DELETE`
function prepareURL(
  collectionName: string,
  idApp: string,
  accessToken: string,
): string {
  const baseURL = process.env.server_url;
  const trustKey = process.env.trustkey;

  if (collectionName === "app") {
    return `${baseURL}/api/app/${idApp}?access_token=${accessToken}&trustkey=${trustKey}`;
  }

  const _idApp = idApp ? `/${idApp}` : "";
  return `${baseURL}/api${_idApp}/${collectionName}?access_token=${accessToken}&trustkey=${trustKey}`;
}

//  Thêm options vào URL cho GET request
function appendOptionsToURL(url: string, options: any): string {
  if (!url) return "";
  let appendedURL = url + `&page=${options.page || 1}`;
  if (options.sort) appendedURL += `&sort=${JSON.stringify(options.sort)}`;
  if (options.condition)
    appendedURL += `&q=${JSON.stringify(options.condition)}`;
  if (options.fields) appendedURL += `&fields=${options.fields}`;
  if (options.limit) appendedURL += `&limit=${options.limit}`;
  if (options.notfields) appendedURL += `&notfields=${options.notfields}`;
  if (options.count) appendedURL += `&count=1`;

  return appendedURL;
}
//#endregion
